import BigNumber from 'bignumber.js';
import Fraction from 'fraction.js';
import dayjs, { OpUnitType } from 'dayjs';
import { STATUS } from '@/components/uploader/props';
import { Message } from '@arco-design/web-vue';
import globalContants from '@/constants/globalContants';
import areaJson from '@/constants/json/area.json';
import store from '@/store';
import useUserStore from '@/store/modules/user/index';
import { storeToRefs } from 'pinia';
import { cloneDeep, uniq, isNumber } from 'lodash';
import orderState, { StateInfo } from '@/constants/order';
import type { RouteMeta } from 'vue-router';
import { KEYBOARD_KEY } from '@/constants/keyboard';

const order: Recordable = {
  // STOCKFLOW: 'stockFlowState', // 库存流水列表单据状态
  OUTBOUNDORDER: 'outboundOrderState', // 出库单列表单据状态
  GOODSREWORKORDER: 'goodsReworkOrderState', // 出库单列表单据状态
  MATERIALINVENTORY: 'materialInventoryState', // 物料盘点单列表单据状态
  MATERIALTRANSFER: 'materialTransferState', // 物料调拨单列表单据状态
  MATERIALPICKING: 'materialPickingState', // 物料领料单列表单据状态
  MATERIALRETURN: 'materialReturnState', // 物料退料单列表单据状态
  FINISHTRANSFER: 'finishedTransferState', // 成品调拨单列表单据状态
  BORROWORDERSATET: 'borrowOrderState', // 借还单列表单据状态
  BACKORDERSATET: 'backOrderState', // 借还单列表单据状态
  BOMISSUESTATE: 'bomIssueState', // 生产单-bom的发料状态
  DESIGNPROSTATE: 'designProState', // 设计款-旗舰版状态
};

const userStore = useUserStore(store);
const { materialDecimal } = storeToRefs(userStore);
// 除法
export function accDiv(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.div(arg2).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.div(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 乘法
export function accMul(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.multipliedBy(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.multipliedBy(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 加法
export function accAdd(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.plus(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.plus(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 减法
export function accSub(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.minus(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.minus(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 取余
export function accMod(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.mod(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.mod(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

/**
 * 获取时间范围的unix对象
 * @param {Array} timeRange
 */
export function getUnixFromStr(timeRange: [string, string]) {
  return {
    begin: timeRange && timeRange.length === 2 ? dayjs(timeRange[0]).unix() : 0,
    end: timeRange && timeRange.length === 2 ? dayjs(timeRange[1]).unix() : 0,
  };
}
// 获取最近30天的日子，用于日期范围默认值
export function getMonthRange() {
  return [
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ];
}
/**
 * 获取某个月的开始时间截止到今天
 * @param t
 */
export const someMonth = (t = new Date(), mode: OpUnitType = 'month') => {
  const beginDateTime = dayjs(t).startOf(mode).valueOf();
  const endDateTime = dayjs().endOf('day').valueOf();

  return [beginDateTime, endDateTime];
};
/**
 * 往前几个月
 */
export const prevMonth = (t: number) => {
  const startTime = dayjs().subtract(t, 'month').startOf('month').valueOf();
  const endTime = dayjs().endOf('day').valueOf();
  return [startTime, endTime];
};

/**
 * @description: 获取n月前指定日期
 * @param {*} n
 * @return {*} Unix Time Stamp e.g: 1652054400000
 */
export const getMonthAgoDate = (n = 0) => {
  const curDate = new Date();
  curDate.setMonth(curDate.getMonth() - n);
  return new Date(curDate).setHours(0, 0, 0, 0);
};

/**
 * 获取间隔num天
 * @param num 间隔天数：从今天开始往前推，默认1天则今天到今天
 * @return Array  返回开始和结束时间的数组
 */
export const getRecentDays = (num: number, format?: string) => {
  const start = getNewDate();
  const end = getNewDate();
  start.setDate(start.getDate() - num + 1);
  if (format) {
    return [dayjs(start).format(format), dayjs(end).format(format)];
  }
  return [start, end];
};
/**
 * 获取日期的年月日零点的Date，用于替换new Date原生的toLocaleDateString方法，解决toLocaleDateString在安卓显示bug
 * @param {Date} date
 * @param {String} format
 * @returns Date
 */
export const getNewDate = (date = new Date(), format = 'YYYY-MM-DD') => {
  // 相当于new Date(new Date().toLocaleDateString())
  return new Date(dayjs(date).format(format));
};
// 获取当天日期，用于日期选择器默认值
export function getToday() {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * 获取某天的开始时间，即0点
 * @param {Date} date
 */
export function getBeginningTime(
  date: string | number | Date = new Date()
): number {
  return new Date(date).setHours(0, 0, 0, 0);
}
export function getHoursTime(
  date: string | number | Date,
  hours: number,
  min?: number,
  sec?: number,
  ms?: number
): number {
  if (!date) date = new Date();
  return new Date(date).setHours(hours, min, sec, ms);
}

/**
 * 获取某天的结束时间，即23:59:59点
 * @param {Date} date
 */
export function getEndingTime(
  date: string | number | Date = new Date()
): number {
  return new Date(date).setHours(23, 59, 59, 999);
}

export function getDiffDays(start: number, end: number) {
  const diffMs = end - start;
  const remainingMs = diffMs % (1000 * 60 * 60 * 24);
  const halfDayPrecision = remainingMs >= 1000 * 60 * 60 * 12 ? 0.5 : 0; // 如果超过半天则加0.5，否则不加
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  // 返回相差的天数，包括半天的精度
  return diffDays + halfDayPrecision;
}
// 结合以上两个，传时间范围数组
export function rangeTime(arr: (string | number | Date)[]): Array<number> {
  if (arr?.length < 2) {
    return [];
  }
  const [start, end]: (string | number | Date)[] = arr;
  const a = [getBeginningTime(start), getEndingTime(end)];
  return a;
}

// 获取默认时间范围数组（最近30天）往前推29+今天
export function getDefaultRangeTime(num = 30): Array<number> {
  const a = [getBeginningTime(getBeforeNumDay(num - 1)), getEndingTime()];
  return a;
}
// 获取相对于当前时间的前几天
export function getBeforeNumDay(days: number): number {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.getTime();
}

/* 传给后端的时间范围是要把结束时间完后推一天，表示包含当天的时间范围
开始时间往前推8小时，结束时间往后推16小时（表示包含当天） */
export const timeZoneBack = (
  format?: string,
  start = new Date(),
  end = new Date()
) => {
  start = getNewDate(start);
  end = getNewDate(end);
  start.setHours(start.getHours() - 8);
  end.setHours(end.getHours() + 16);
  if (format) {
    return [dayjs(start).format(format), dayjs(end).format(format)];
  }
  return [start, end];
};

export function isDev() {
  return (
    window.location.host.indexOf('devscm') > -1 ||
    window.location.host.indexOf('localhost') > -1 ||
    window.location.host.indexOf('127.0.0.1') > -1
  );
}
/**
 * 获取几天后的时间
 * @param {n} 天数
 * @param {Date} date
 */
export function getAfterDate(
  n: number,
  date: string | number | Date = new Date(),
  zero = true
): number {
  return (
    new Date(zero ? getBeginningTime(date) : date).getTime() +
    n * 3600 * 24 * 1000
  );
}

/**
 *
 * @param {string | number | Date | Dayjs} dateType 日期
 * @param {string} format 日期格式
 * @param {string} placeHolder 占位符
 * @returns
 */
export function formatTime(
  date: string | number | Date,
  format = 'YYYY-MM-DD HH:mm',
  placeHolder = '-'
): string {
  if (!date) return placeHolder;
  return dayjs(date).format(format);
}

export function isObject(value: any) {
  return typeof value === 'object' && value !== null;
}

/**
 * @param {*} dataAttrIdKey 'sizeId'
 * @param {*} dataAttrNameKey 'sizeName'
 * @param {*} dataList [{sizeName: 'XL', sizeId: 12}]
 */
export function handleCommonList(
  dataAttrIdKey: string,
  dataAttrNameKey: string,
  dataList = []
) {
  let list: any = [];
  dataList.forEach((item: any) => {
    // XL
    const attrIdValue = item[dataAttrIdKey];
    // 12
    const attrNameValue = item[dataAttrNameKey];
    // 排除相同的
    if (list.every((data: any) => data.id !== attrIdValue)) {
      list = [
        ...list,
        {
          id: attrIdValue,
          name: attrNameValue,
        },
      ];
    }
  });
  return list;
}

/**
 * @param {Array} dataList [{colorName: '红色', colorId: 21, sizeName: 'XL', sizeId: 18, ...}]
 * @param {*} keys
 * @param {*} addKeys
 * @param {*} keyIncludeText key是否包含文本
 * @returns Array
 */
export function handleTwoIdList(
  dataList: any = [],
  keys: any = {},
  addKeys: any = ['skuBarCode', 'id'],
  keyIncludeText = false
) {
  const list: any = {};
  const defaultKes = {
    firIdKey: 'colorId',
    firIdVal: 'colorName',
    secIdKey: 'sizeId',
    sedIdVal: 'sizeName',
  };
  keys = Object.assign(defaultKes, keys);
  const { firIdKey, firIdVal, secIdKey, sedIdVal } = keys || defaultKes;
  dataList.forEach((item: any) => {
    const firIdKeyData = item[firIdKey]; // 21
    const firIdValData = item[firIdVal]; // 红色
    const secIdKeyData = item[secIdKey]; // 18
    const sedIdValData = item[sedIdVal]; // XL
    const temp = {
      [`${firIdKey}`]: firIdKeyData,
      [`${firIdVal}`]: firIdValData,
      [`${secIdKey}`]: secIdKeyData,
      [`${sedIdVal}`]: sedIdValData,
    };
    const keyName = keyIncludeText
      ? `${firIdKeyData}_colorId`
      : `${firIdKeyData}`;
    if (Object.keys(list).some((firId: any) => `${firId}` === keyName)) {
      Object.entries(list).forEach((firMapSec: any) => {
        const firId = firMapSec[0];
        addKeys.forEach((key: any) => {
          temp[key] = item[key];
        });
        if (`${firId}` === keyName) {
          firMapSec[1][`${secIdKeyData}`] = temp;
        }
      });
    } else {
      addKeys.forEach((key: any) => {
        temp[key] = item[key];
      });
      list[keyName] = {
        [`${secIdKeyData}`]: temp,
      };
    }
  });
  return list;
}

/**
 * @param {Array} dataList [{colorName: "红色", sizeName: 'XL', number: 15}]
 * @param {string} columnFileKey 首列 column Filed 如 color
 * @param {string} columnValKey  首列 column Filed 使用的 dataList 中的key 如 colorName
 * @param {string} rowAttrKey 非首列 使用的dataList 中的属性的key 如 sizeId
 * @param {string} rowValKey  非首列 使用的dataList 中的值的key 如 number
 * @param {any} defRowValKey rowValKey 为 undefined 的替代品
 * @returns Array
 */
export function handleTableData(
  dataList: Record<string, any>[] = [],
  columnFileKey = 'color',
  columnValKey = 'colorName',
  rowAttrKey = 'sizeId',
  rowValKey = 'number',
  defRowValKey: string | number | undefined = '',
  isCopy = false // isCopy为true时将不会对data的rowKey赋值（不会执行data[rowKey] = rowVal;
) {
  let tableData: any = [];
  dataList.forEach((item: any) => {
    const columnVal = item[columnValKey];
    const columnId = `${columnFileKey}Id`;
    const rowKey = item[rowAttrKey];
    const rowVal = item[rowValKey] || defRowValKey;
    if (tableData.some((data: any) => data[columnFileKey] === columnVal)) {
      if (!isCopy) {
        tableData.forEach((data: any) => {
          if (data[columnFileKey] === columnVal) {
            data[rowKey] = rowVal;
          }
        });
      }
    } else {
      const itemTemp = {
        [columnFileKey]: columnVal,
        [rowKey]: rowVal,
        [columnId]: item[columnId],
      };
      if (isCopy) delete itemTemp[rowKey];
      tableData = [...tableData, itemTemp];
    }
  });
  return tableData;
}

/**
 * 根据sku列表找出color和size的id->name的map对象
 * @param dataList
 * @returns
 */
export function handleColorAndSizeMap(dataList: Record<string, any>[] = []) {
  const color: Record<string, any> = {};
  const size: Record<string, any> = {};
  dataList.forEach((sku: Record<string, any>) => {
    const { colorId, colorName, sizeId, sizeName } = sku;
    if (colorId && !color?.[colorId]) color[colorId] = colorName;
    if (sizeId && !size?.[sizeId]) size[sizeId] = sizeName;
  });
  return { color, size };
}

export function getFileType(text: any) {
  let type;
  switch (text) {
    case 'image':
      type = 1;
      break;
    case 'video':
      type = 2;
      break;
    default:
      type = 3;
      break;
  }
  return type;
}

// 获取文件后缀名称
export function getFileSuffix(name: any) {
  let fileSuffix = '';
  if (name?.split) {
    fileSuffix = name?.split('.').pop();
  }
  return fileSuffix;
}
export function getTypeBySuffix(suffix: string) {
  const imageSuffix = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'tif',
    'tiff',
    'webp',
    'svg',
  ];
  const videoSuffix = ['mp4', 'avi', 'wmv', 'flv', 'mov', 'mkv', 'mpeg', 'mpg'];
  suffix = typeof suffix === 'string' ? suffix.toLowerCase() : suffix;
  if (imageSuffix.includes(suffix)) {
    return 'image';
  }
  if (videoSuffix.includes(suffix)) {
    return 'video';
  }
  return suffix;
}

/**
 * 处理附件
 * @param {Array} list 附件列表
 * @param {Object} params 需要给附件额外加上的参数，如单号，物料Id等
 * @returns Array
 */

export function handleFile(
  list: Record<string, any>[],
  params: Record<string, any> = {}
) {
  return handleFileOrImages(list, 'file', params);
}
/**
 * 处理图片
 * @param {Array} list 图片列表
 * @param {Object} params 需要给图片额外加上的参数，如单号，物料Id等
 * @returns Array
 */

export function handleImages(
  list: Record<string, any>[],
  params: Record<string, any> = {}
) {
  return handleFileOrImages(list, 'images', params);
}

/**
 * 处理图片 OR 附件
 * @param {Array} list 图片或附件列表
 * @param {Object} params 需要给图片额外加上的参数，如单号，物料Id等
 * @returns Array
 */
export function handleFileOrImages(
  list: Record<string, any>[],
  handleType = 'images',
  params: Record<string, any> = {}
) {
  if (!Array.isArray(list)) return [];
  if (!isObject(params)) {
    params = {};
  }
  const tempLlist: Record<string, any>[] = [];
  list.forEach((item, index) => {
    const { fileId, name, fileName, url, id, type } = item;
    const temp: Record<string, any> = {
      type,
      fileId,
      url,
      showOrder: index,
    };
    if (handleType === 'images') {
      temp.name = name || fileName;
    }
    if (handleType === 'file') {
      temp.fileName = fileName || name;
    }
    if (id || id === 0) {
      temp.id = id;
    }
    Object.assign(temp, params);
    tempLlist.push(temp);
  });
  return tempLlist.filter((e) => Boolean(e.url));
}
/**
 * 检查附件列表是否存在“上传中”状态的附件
 * @param {Array} list
 * @returns Boolean
 */
export function checkUploaderStatus(
  list: Record<string, any>[],
  showToast = true
) {
  if (list.some((file) => file?.status === STATUS.fail)) {
    if (showToast === true) Message.error('附件上传失败，请检查！');
    return true;
  }
  const status = list.some((file) => file?.status === STATUS.uploading);
  if (status && showToast === true) {
    Message.warning('附件上传中！');
  }
  return status;
}
/*
 * @param {Arrat} dataList [{colorName: "红色", sizeName: 'XL', number: 15}...]
 * @param {string} dataAttrKey data 中的key，其data[key] 作为 field 的 value
 * @param {Object} firstColumn 首列
 * @param {Object} lastColumn 最后一列
 * @returns Array
 */
export const handleTableColumns = (
  dataList: any,
  dataAttrKey: string,
  firstColumn: object | undefined,
  lastColumn: object | undefined,
  columnIdKey: any,
  idKeys = ['id'],
  extraColumnField: Record<string, any> | undefined = {}
) => {
  let columns: any = [];
  // 如果存在首列
  if (firstColumn) {
    columns = [firstColumn];
  }
  dataList.forEach((item: any) => {
    const columnKey = item[dataAttrKey];
    const columnId = `${item[columnIdKey]}`;
    const temp: any = {
      field: columnId,
      key: columnId,
      title: columnKey,
      width: 135,
      default: 0,
    };
    Object.assign(temp, extraColumnField);
    idKeys.forEach((key: any) => {
      temp[`${key}`] = item[`${key}`];
    });
    // 如果没有存在此列
    if (columns.every((data: any) => data.field !== columnId)) {
      columns = [...columns, temp];
    }
  });
  // 如果存在最后一列
  if (lastColumn) {
    columns = [...columns, lastColumn];
  }
  return columns;
};

// 删除中文
export const removeChineseText = (value: string) =>
  value.replace(/[\u4E00-\u9FA5]/g, '');

// 只允许输入正整数
export const onlyPositiveInteger = (value: any) => {
  let result = value;
  result = Number(result.replace(/\D/g, '')) as number;
  // 输入不可以大于最大整数
  if (result > globalContants.MAXNINEDIGITNUM) {
    result = globalContants.MAXNINEDIGITNUM;
  }
  return result;
};

// 选择地址统一已处理
export const areaList = (function areaJsonFn() {
  const areaListTmp: any = {
    province_list: {},
    city_list: {},
    county_list: {},
  };
  areaJson.forEach((item: any) => {
    areaListTmp.province_list[item.id] = item.areaName;
    item.children.forEach((city: any) => {
      areaListTmp.city_list[city.id] = city.areaName;
      city.children?.forEach((county: any) => {
        areaListTmp.county_list[county.id] = county.areaName;
      });
    });
  });
  return areaListTmp;
})();

/**
 * 判断是否为正确的手机号码
 * @param cellPhoneNo 手机号
 * @param prefix 前缀
 * @returns {*|boolean}
 */
export function isValidCellPhoneNo(cellPhoneNo: any, prefix?: any) {
  let regExp: any = '';
  if (prefix === '' || prefix === undefined) {
    regExp = /^1[3456789]\d{9}$/;
  } else {
    regExp = /^0?[0-9]{8,10}$/;
  }
  return regExp.test(cellPhoneNo);
}

// 支持负数
export function formatterNegative(value: any, decimal: string | number = 2) {
  return formatter(value, decimal, true);
}

// 不支持负数
export function formatter(
  value: any,
  decimal: string | number,
  negative?: boolean // 是否能格式化负数
) {
  if (value === undefined) return undefined;
  if (decimal === undefined) decimal = 2;
  if (typeof value !== 'string') value = value.toString();
  // 如果第一位是0且第二位不是小数点，则去掉0
  if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
    value = value.substring(1);
  }
  if (value || value === '0') {
    // 如果有小数位
    if (value.indexOf('.') !== -1) {
      value = value.substring(0, value.indexOf('.') + decimal + 1);
    }
    // 不能为负数
    if (!negative && /^-/.test(value)) {
      value = value.substring(1);
    }
    const temp = Number(value);
    // 输入不可以大于最大整数
    if (temp > globalContants.MAXNINEDIGITNUM) {
      value = globalContants.MAXNINEDIGITNUM;
    }
    return temp;
  }
  return undefined;
}

// 四舍五入格式化小数点
export function formatterRound(
  value: any,
  decimal: number,
  negative?: boolean // 是否能格式化负数
) {
  if (decimal === undefined) decimal = 2;
  const decimalOneMorePerson = decimal + 1; // 多加一位是为了四舍五入
  const formatterValue = formatter(value, decimalOneMorePerson, negative);
  if (formatterValue === undefined) return undefined;
  return roundDecimal(formatterValue, decimal); // 使用实际的小数位四舍五入
}

// 按配置小数位和四舍五入格式化金额
export const priceDecimalFormatter = ({ cellValue }: any): string => {
  if (cellValue === 0) return `${globalContants.CURRENCYSYMBOL}0`;
  return `${globalContants.CURRENCYSYMBOL}${formatterRound(
    cellValue,
    materialDecimal.value
  )}`;
};

// 获取选择地址json
export const selectAreaData = (function getdata() {
  const areaJsonList = cloneDeep(areaJson);
  const deepChildren = (list: any[]) => {
    list.forEach((item: any) => {
      item.value = item.id;
      item.label = item.areaName;
      delete item.id;
      delete item.areaName;
      if (item?.children?.length) {
        deepChildren(item.children);
      } else {
        delete item.children;
      }
    });
  };
  deepChildren(areaJsonList);
  return areaJsonList;
})();
export const getAddress = (areaObj: Recordable, hasAddress = false) => {
  // 省 市 区 详细地址
  const {
    recipientProvincesId,
    recipientCityId,
    recipientCountyId,
    address = '',
  } = areaObj;
  // 地区数据回显成字符串
  let area = '';
  const province = areaList.province_list[recipientProvincesId];
  const city = areaList.city_list[recipientCityId];
  const county = areaList.county_list[recipientCountyId];
  if (!province) {
    area = '';
  } else if (!city) {
    area = province;
  } else if (!county) {
    area = `${province}-${city}`;
  } else {
    area = `${province}-${city}-${county}`;
  }
  if (address && hasAddress) {
    area = area ? `${area},${address}` : address;
  }
  return area || '';
};

/**
 * 截取两位小数
 * @param value 输入的数字 或可转数字的字符串 2.136 => 2.13 5 => 5
 */
export function cutTwoDecimal(value: string | number | undefined) {
  return cutDecimal(value, 2);
}
export function cutDecimal(
  value: string | number | undefined,
  decimal?: number,
  isNegative?: boolean // 是否支持格式化负数，之前不支持
) {
  if (value === undefined) return 0;
  decimal = decimal || materialDecimal.value;
  // eslint-disable-next-line no-useless-escape
  const reg = isNegative
    ? RegExp(`^-?\\d+(?:\\.\\d{0,${decimal}})?`)
    : RegExp(`^\\d+(?:\\.\\d{0,${decimal}})?`);
  value = Number(value.toString().match(reg));
  return value;
}

// 支持格式化负数
export function cutNegativeDecimal(
  value: string | number | undefined,
  decimal?: number
) {
  return cutDecimal(value, decimal, true);
}

/**
 * 四舍五入截取两位小数
 * @param value 输入的数字 或可转数字的字符串 2.136 => 2.14
 */
export function roundTwoDecimal(value: string | number | undefined) {
  return roundDecimal(value, 2);
}

export function roundDecimal(
  value: string | number | undefined,
  decimal?: number
) {
  if (Number.isNaN(value)) return 0;
  decimal = decimal ?? materialDecimal.value;
  value =
    accDiv(
      Math.round(accMul(Number(value), 10 ** decimal) ?? 0),
      10 ** decimal
    ) ?? 0;
  return value;
}

export function getObject(arr: Recordable[], key: string, value: string) {
  if (!Array.isArray(arr)) return undefined;
  return arr.find((item: Recordable) => item[key] === value);
}

/**
 * 单据状态格式化过滤器
 * @param input 当前项的值
 * @param arr 所有值数组
 * @param keyName 传入input值对应的键名,默认值为'val'
 * @param valName 需要获取的值对应的键名,默认值为'text'
 * @return text  返回valName键名对应的值
 */
export function orderSource(
  input: any,
  type: any,
  keyName = 'val',
  valName = 'text'
) {
  if (input !== undefined) {
    const accType = ((orderState as Recordable)[order[type]] || []).find(
      (e: any) => e[keyName] === input
    );
    let showString = '';
    if (accType !== undefined) {
      showString = accType[valName];
    }
    return showString;
  }
  return input;
}

/**
 * 将金额的数值进行处理，每隔三位数字，用逗号分隔开，并且保留两位小数，默认加上系统货币符号
 * @param value 需要格式化的值
 * @param notCurrencySymbol 是否需要金额符号
 * @param decimal 保留位数
 * @return 返回格式化后的结果
 */
export function numberToCurrency(
  value: string | number,
  notCurrencySymbol = false,
  decimal = 2
) {
  let symbol = globalContants.CURRENCYSYMBOL;
  let result = '';
  let addCode = '';
  if (notCurrencySymbol) {
    // 有值则说明不需要货币符号
    symbol = '';
  }
  if (!value) {
    result = '0.00';
  } else {
    const intPart = parseInt(value.toString(), 10) ?? 0; // 获取整数部分
    const stringVal = Object.is(intPart, -0) ? '-0' : intPart.toString();
    const intPartFormat = stringVal.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,'); // 将整数部分每三位加一个逗号
    let floatPart: string | number = '.'.padEnd(decimal + 1, '0'); // 预定义小数部分
    if (typeof value !== 'string') {
      value = value.toString();
    } else if (intPart > 0 && value.includes('+')) {
      value = value.replace('+', '');
      addCode = '+';
    }
    const value2Array = value.split('.');
    //= 2表示数据有小数位
    if (value2Array.length === 2) {
      floatPart = value2Array[1].toString(); // 拿到小数部分
      if (floatPart.length === 1) {
        // 若小数位数只有一位，补0
        result = `${intPartFormat}.${floatPart}0`;
      } else if (floatPart.length > decimal) {
        // 若小数位数在两位以上，则四舍五入保留两位小数
        floatPart = new BigNumber(`0.${floatPart}`)
          .toFixed(decimal)
          .replace('0.', '');
        // 如果是0.9966等类型四舍五入会变成1.00，此时单独处理
        // parseInt(floatPart, 10) === 1 —— 如果是0.008四舍五入为01 parseInt会为1,所以要区分floatPart为1.00、1.000、01、001的情况
        const isBigDecimal = floatPart.toString()[0] === '1';
        result =
          parseInt(floatPart, 10) === 1 && isBigDecimal
            ? (accAdd(stringVal || 0, 1)?.toString() as string)
            : `${intPartFormat}.${floatPart}`;
      } else {
        result = `${intPartFormat}.${floatPart}`;
      }
    } else {
      // 数据为整数
      result = intPartFormat + floatPart;
    }
  }
  return symbol + addCode + result;
}
/**
 * 将金额的数值进行处理，每隔三位数字，用逗号分隔开，并且保留两位小数，默认加上系统货币符号
 * @param amount 需要格式化的值
 * @param notCurrencySymbol 是否需要金额符号
 * @param decimal 保留位数
 * @return 返回格式化后的结果
 */
export function formatAmount(
  amount: number,
  notCurrencySymbol = false,
  decimalPlaces = 2
): string {
  let symbol = globalContants.CURRENCYSYMBOL;
  if (notCurrencySymbol) {
    // 有值则说明不需要货币符号
    symbol = '';
  }
  const formattedAmount = new BigNumber(amount).toFixed(decimalPlaces); // 保留指定位数的小数
  const parts = formattedAmount.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); // 正则表达式添加千分位逗号
  if (decimalPlaces > 0) {
    return `${symbol}${integerPart}.${parts[1]}`;
  }
  return `${symbol}${integerPart}`;
}

/* 组件date选出来的时间是0点，产品要新增的时候只选年月日，详情又要显示为年月日和当前的时间
 * @param {Date} businessTime
 * @returns 日期与businessTime相同，时间为当前时间的Date
 */
export const replaceWithCurrentTime = (businessTime: string | Date) => {
  const date = new Date(businessTime);
  const now = new Date();
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds()
  );
};

// 深拷贝对象
export function deepClone(obj: any) {
  const toStringFn = Object.prototype.toString;

  // null, undefined, non-object, function
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // DOM节点
  if (obj.nodeType && 'cloneNode' in obj) {
    return obj.cloneNode(true);
  }

  // Date对象
  if (toStringFn.call(obj) === '[object Date]') {
    return new Date(obj.getTime());
  }

  // RegExp对象
  if (toStringFn.call(obj) === '[object RegExp]') {
    const flags = [];
    if (obj.global) {
      flags.push('g');
    }
    if (obj.multiline) {
      flags.push('m');
    }
    if (obj.ignoreCase) {
      flags.push('i');
    }

    return new RegExp(obj.source, flags.join(''));
  }

  let result: any;
  if (Array.isArray(obj)) {
    result = [];
  } else if (obj.constructor) {
    result = new obj.constructor();
  } else {
    result = {};
  }

  Object.keys(obj).forEach((key: string) => {
    result[key] = deepClone(obj[key]);
  });

  return result;
}

// 检查是否有权限
export const checkAuthority = (
  roles: string[] | string,
  permissionMode: RouteMeta['permissionMode'] = 'every',
  toast = false
) => {
  const authorityList = Array.isArray(roles) ? roles : [roles];
  const { permissions = [], permissionRemarkMap = {} } = userStore;
  if (Object.keys(permissionRemarkMap)?.length === 0 && authorityList.length) {
    // 说明获取不到权限则清除用户信息，再下次跳转路由时会再次getCurrentUser获取用户信息
    userStore.resetInfo();
  }
  const noAuthorityList: string[] = authorityList.filter(
    (item: string) => !permissions.includes(item)
  );
  const check =
    permissionMode === 'every'
      ? noAuthorityList.length === 0
      : noAuthorityList.length < authorityList.length;
  if (!check && toast === true) {
    Message.error(
      `权限不足，请联系管理员设置「${noAuthorityList
        .map((e) => permissionRemarkMap[e])
        .join(permissionMode ? ' | ' : '、')}」权限`
    );
  }
  return check;
};

// 检查是否有权限并提示
export const checkAuthorityToast = (
  roles: string[] | string,
  permissionMode: RouteMeta['permissionMode'] = 'every'
) => {
  return checkAuthority(roles, permissionMode, true);
};

// 将一个sheet转成最终的excel文件的blob对象，然后利用URL.createObjectURL下载
export const sheet2blob = (sheet: any, sheetName = 'sheet1') => {
  const workbook: {
    SheetNames: string[];
    Sheets: any;
  } = {
    SheetNames: [sheetName],
    Sheets: {},
  };
  workbook.Sheets[sheetName] = sheet;
  // 生成excel的配置项
  const wopts = {
    bookType: 'xlsx', // 要生成的文件类型
    bookSST: false, // 是否生成Shared String Table，官方解释是，如果开启生成速度会下降，但在低版本IOS设备上有更好的兼容性
    type: 'binary',
  };
  const wbout = window.XLSX.write(workbook, wopts);
  const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });

  // 字符串转ArrayBuffer
  function s2ab(s: string) {
    const buf = new ArrayBuffer(s.length * 2);
    const view: any = new Uint8Array(buf);
    let i = 0;
    while (i < s.length) {
      view[i] = s.charCodeAt(i);
      i += 1;
    }
    return buf;
  }

  return blob;
};

// 导出excel文件
export const exportDataToExcel = (exportList: any[], fileName: string) => {
  const sheet = window.XLSX.utils.json_to_sheet(exportList);
  const url: any = sheet2blob(sheet);
  if (typeof url === 'object' && url instanceof Blob) {
    const str = `${fileName}.xlsx`;
    const filename = decodeURIComponent(str);
    // 创建一个临时的url指向blob对象
    const objectUrl = window.URL.createObjectURL(url);
    // 创建url之后可以模拟对此文件对象的一系列操作，例如：预览、下载
    const a = document.createElement('a');
    a.setAttribute('href', objectUrl);
    a.setAttribute('download', filename);
    a.click();
    // 5.释放这个临时的对象url
    window.URL.revokeObjectURL(objectUrl);
  }
};
// 生成随机数id
export const generateRandomId = () => {
  return `id-${new Date().getTime().toString(36)}-${Math.random()
    .toString(36)
    .substring(2, 9)}`;
};

// 压缩图片：pclist goodalbum pic200wh等
export function dealWithPic(imgurl: string, alias: string) {
  if (!imgurl) return imgurl;
  // 已经使用了别名的图片
  if (
    imgurl?.split('.')[1] &&
    (imgurl?.split('.')[1]?.indexOf('x-image-process') > -1 ||
      imgurl.split('.')[1].indexOf('@!') > -1)
  ) {
    return imgurl;
  }
  if (imgurl?.indexOf('.gif') > -1) {
    // auto-orient 会使gif动效失效，所以gif不进行图片处理
    return imgurl;
  }
  // 使用别名的规则进行压缩图片-SCM图片
  if (
    (imgurl?.indexOf('is-scm-dev.cdn.qinsilk.com') > -1 ||
      imgurl?.indexOf('is-scm.cdn.qinsilk.com') > -1) &&
    alias
  ) {
    if (imgurl?.indexOf('?') === -1) {
      return `${imgurl}?x-image-process=style/${alias}`;
    }
    return imgurl;
  }
  // 使用别名的规则进行压缩图片-进销存图片（同步进销存商品）
  if (imgurl?.indexOf('qinsilk.com') > -1 && alias) {
    if (imgurl?.indexOf('?') === -1) {
      return `${imgurl}@!${alias}`;
    }
    return imgurl;
  }
  return imgurl;
}

// 判断当前页面有没有打开的弹窗
export const isUseModal = () => {
  return (
    Array.from(document.querySelectorAll('.arco-modal-container'))?.some(
      (e: Recordable) => {
        return e?.style?.display !== 'none';
      }
    ) ?? false
  );
};

// 根据字段对数组分组
export const groupByField = (list: Recordable[], field: string) => {
  return list.reduce((acc, item) => {
    const key = item[field];
    if (acc[key]) {
      acc[key].push(item);
    } else {
      acc[key] = [item];
    }
    return acc;
  }, {});
};

// 根据系统配置显示和隐藏菜单入口
export const showMenuBySycConfig = (
  sysConfigMap: Recordable,
  menu: RouteMeta
) => {
  if (!menu) return true;
  const { sysConfigKey, defaultValue = true } = menu;
  if (!sysConfigKey) return true;
  const numberValue = Number(sysConfigMap[sysConfigKey]);
  const value = Number.isNaN(numberValue) ? defaultValue : numberValue;
  return !!value;
};

// 用户div等元素使用快捷键模拟点击（比如div获取焦点后使用enter模拟点击）
export const imitateTheClickEnterByKeydown = (
  e: any,
  querySelectorStr?: string,
  KEY = KEYBOARD_KEY.ENTER
) => {
  if (e?.key === KEY) {
    e?.preventDefault();
    const targetEl = querySelectorStr
      ? e?.target?.querySelector(querySelectorStr)
      : e?.target;
    targetEl?.dispatchEvent(new Event('click'));
  }
};
// format 默认精度为天数
export const getDiffDate = (
  date: Date,
  target: Date = new Date(),
  format = 'YYYY-MM-DD'
) => {
  const src = dayjs(date).format(format);
  const nowDayjs = dayjs(target).format(format);
  return dayjs(src).diff(dayjs(nowDayjs), 'day');
};

/**
 * 根据商品和物料关联关系的单品列表计算单件用量（spu | sku）
 * @param goodsMaterialList 物料单品列表
 * @param field materialId | materialSkuId 计算不同维度的单件用量
 * @returns
 */
export const getSingleQuantity = (
  goodsMaterialList: Recordable[],
  field = 'materialId'
) => {
  const data: Recordable[] = [];
  const materialObj: Recordable = {};
  // 有物料用量的商品单品数
  const goodsSkuObj: Recordable = {
    sku: {},
    number: 0,
    totalCost: 0,
  };
  goodsMaterialList?.forEach((sku: Recordable) => {
    const {
      price = 0,
      number = 0,
      goodsColorId,
      goodsSizeId,
      imgUrl,
      sn,
      unitName,
      name,
    } = sku;
    if (!number) return;
    const fieldVal = sku[field];
    if (materialObj[fieldVal]) {
      materialObj[fieldVal].number = accAdd(
        materialObj[fieldVal].number,
        number,
        4
      );
      materialObj[fieldVal].cost = accAdd(
        materialObj[fieldVal].cost,
        accMul(number, price, 4),
        4
      );
    } else {
      materialObj[fieldVal] = {
        number,
        cost: accMul(number, price, 4),
        sku: {},
        skuNumber: 0,
      };
      data.push({
        [field]: fieldVal,
        imgUrl,
        sn,
        unitName,
        name,
      });
    }
    if (!materialObj[fieldVal]?.sku?.[`${goodsColorId}-${goodsSizeId}`]) {
      // 累加单品数
      materialObj[fieldVal].sku[`${goodsColorId}-${goodsSizeId}`] = true;
      materialObj[fieldVal].skuNumber += 1;
    }
    if (!goodsSkuObj.sku?.[`${goodsColorId}-${goodsSizeId}`]) {
      // 累加单品数
      goodsSkuObj.sku[`${goodsColorId}-${goodsSizeId}`] = true;
      goodsSkuObj.number += 1;
    }
  });
  data.forEach((material: Recordable) => {
    const item = materialObj[material[field]];
    if (!item) return;
    const { skuNumber, cost } = item;
    // 单件用量 = 物料规格1所有成衣用量+...+物料规格n所有成衣用量）/该物料涉及成衣数
    material.singleQuantity = skuNumber
      ? accDiv(item.number, skuNumber, 4) || 0
      : 0;
    material.number = item.number; // 物料总用量
    material.costTotal = cost; // 物料总成本
    material.avgCost = accDiv(cost, skuNumber, 4); // 单件成本 = 该物料所有成本 / 该物料涉及成衣数
    // 平均单价 = 单件成本 ÷ 单件用量
    material.avgPrice = accDiv(material.avgCost, material.singleQuantity, 4);
    // 所有物料总成本
    goodsSkuObj.totalCost = accAdd(goodsSkuObj.totalCost, item.cost, 4) || 0;
  });
  return { list: data, materialObj, goodsSkuObj };
};

// 判断撤销抵冲状态
export const getStateInfo = (isRevoke: any, recordType: any) => {
  return StateInfo.find((item: Recordable) =>
    isRevoke === 0
      ? item.isRevoke === isRevoke
      : item.isRevoke === isRevoke && item.recordType === recordType
  );
};

// 处理年份的加减
export const handleYearInfo = (method: string, goodsInfo: Recordable) => {
  if (method === 'decrement') {
    if (goodsInfo.year === '' || goodsInfo.year === undefined) {
      goodsInfo.year = new Date().getFullYear();
    }
    if (goodsInfo.yearRanges === '' || goodsInfo.yearRanges === undefined) {
      goodsInfo.yearRanges = 1;
    }
    if (goodsInfo.yearRanges === 1) {
      goodsInfo.year = accSub(goodsInfo.year, 1, null);
      goodsInfo.yearRanges = 2;
    } else if (goodsInfo.yearRanges === 2) {
      goodsInfo.yearRanges = 1;
    }
  } else if (method === 'increment') {
    if (goodsInfo.year === '' || goodsInfo.year === undefined) {
      goodsInfo.year = new Date().getFullYear();
    }
    if (goodsInfo.yearRanges === '' || goodsInfo.yearRanges === undefined) {
      goodsInfo.yearRanges = 1;
    }
    if (goodsInfo.yearRanges === 1) {
      goodsInfo.yearRanges = 2;
    } else if (goodsInfo.yearRanges === 2) {
      goodsInfo.year = accAdd(goodsInfo.year, 1, null);
      goodsInfo.yearRanges = 1;
    }
  }
};

// 正则精确验证分数小数整数的情况
export const fractionValidate = (value: string) => {
  // 处理分数的情况
  const regex = /^-?\d+\/\d+$|^-?\d+\s?\d+\/\d+$|^-?\d+$|^-?\d+\.\d+$/;
  return regex.test(value);
};
export const getError = (value: any) => {
  if (value) {
    return !fractionValidate(value);
  }
  return false;
};
export function String2ArrayBuffer(str: any) {
  return new Promise((resolve, reject) => {
    const b = new Blob([str]);
    const f = new FileReader();
    f.onloadend = function (e) {
      if (f.error) {
        reject(f.error);
      }
      resolve(f.result);
    };
    f.readAsArrayBuffer(b);
  });
}
// 获取单品价格的范围字符串
export const getPriceRangeBySkuList = (list: number[]) => {
  let minPrice = 0;
  let maxPrice = 0;
  let priceRange = '';
  let priceList: number[] = [];
  const skuPriceList = list.filter(isNumber);
  if (skuPriceList.length) {
    priceList = uniq(skuPriceList);
    priceList.sort((a, b) => a - b);
    if (priceList?.length > 1) {
      [minPrice] = priceList;
      maxPrice = priceList[priceList.length - 1];
      priceRange = `${globalContants.CURRENCYSYMBOL}${minPrice} ~ ${globalContants.CURRENCYSYMBOL}${maxPrice}`;
    } else if (priceList.length) {
      [minPrice] = priceList;
      [maxPrice] = priceList;
      priceRange = `${globalContants.CURRENCYSYMBOL}${minPrice}`;
    }
  }
  return { priceRange, priceList, minPrice, maxPrice };
};

// 物料列表/选择物料-拼接价格范围
export const getPriceRange = (material: Recordable) => {
  if (!material) return `${globalContants.CURRENCYSYMBOL}0`;
  const { enableSkuPrice, price, maxSkuPrice = 0, minSkuPrice = 0 } = material;
  const isZero = (val: any) => {
    return val === 0 ? `${globalContants.CURRENCYSYMBOL}0` : undefined;
  };
  if (!enableSkuPrice)
    return (
      isZero(price) ??
      `${formatAmount(price, undefined, materialDecimal.value)}`
    );
  if (minSkuPrice === maxSkuPrice)
    return (
      isZero(minSkuPrice) ??
      `${formatAmount(minSkuPrice, undefined, materialDecimal.value)}`
    );
  const minPrice =
    isZero(minSkuPrice) ??
    formatAmount(minSkuPrice, undefined, materialDecimal.value);
  const maxPrice =
    isZero(maxSkuPrice) ??
    formatAmount(maxSkuPrice, undefined, materialDecimal.value);
  return `${minPrice} ~ ${maxPrice}`;
};

// 获取物料面料属性名称
export const getFabricTypeName = (fabricType?: number) => {
  if (fabricType === 1) return '针织';
  if (fabricType === 2) return '梭织';
  return undefined;
};
// 物料列表物料类型-面料属性
export const formatterMaterialTypeAndFabricType = ({ row }: any): string => {
  const { materialTypeName, fabricType } = row;
  const fabricTypeName = getFabricTypeName(fabricType);
  if (fabricTypeName) return `${materialTypeName}-${fabricTypeName}`;
  return materialTypeName;
};

/**
 * 根据多单位转换系数列表获取第一个需要的字段
 * @param unitConvertList materialUnitConvertList / materialSkuUnitConvertList
 * @returns 转换系数、采购单价、采购单位、净布价
 */
export const getUnitConvertFields = (unitConvertList: Recordable[]) => {
  const {
    convertRate,
    purchasePrice,
    cleanClothPrice,
    purchaseUnitId,
    purchaseUnitName,
  } = unitConvertList?.[0] ?? {};
  return {
    convertRate,
    purchasePrice,
    cleanClothPrice,
    purchaseUnitId,
    purchaseUnitName,
  };
};

// cid = 578 维克多客户要求工序附件数量增加到50
export const getAttachmentMaxCount = (cid: number) => {
  if (cid === 578) {
    return globalContants.CUSTOMER_ATTACHEMENT_MAX_COUNT;
  }
  return globalContants.ATTACHEMENT_MAX_COUNT;
};

/**
 * 去掉小数点后面多余的 0
 * @param val
 */
export const removeDecimalZero = (val: string) => {
  const regexp = /(?:\.0*|(\.\d+?)0+)$/;
  val = `${val}`.replace(regexp, '$1');
  return val;
};

// export const newFeatureReminderModal = () => {
//   // 生产单 | 商品列表 | 物料列表 | 采购单列表
//   const newFeatureReminder = localStorage.getItem(`newFeatureReminder`);
//   if (newFeatureReminder !== 'true') {
//     Modal.info({
//       title: '新功能提醒',
//       okText: '知道了',
//       hideCancel: true,
//       bodyStyle: { 'color': '#ff0000', 'padding-left': '30px' },
//       content:
//         '新版物料信息、商品BOM、采购单支持基础单位、采购单位的系数转换，无需人工手动换算，实现不同单位自动转换。例如针织面料按照“公斤”采购，但是转换为“米”数入库。如有使用疑问可联系客服。',
//     });
//     localStorage.setItem(`newFeatureReminder`, 'true');
//   }
// };

// 根据面料类型判断是否不显示对应字段值
export const checkMaterialFieldIsShow = (
  fabricType: number,
  field: string,
  otherFields?: string[]
) => {
  // fabricType = 1 针织 2 梭织
  // 如果是【针织/梭织】，则【匹数、纸筒、空差、净重、克重、出米数】数据支持修改；
  if (![1, 2].includes(fabricType)) return false;
  // 针织显示匹数、克重、出米数、纸筒、空差、净重
  return (
    fabricType === 1 &&
    [
      'cloth', // 匹数
      'gramWeight', // 克重
      'materialGramWeight', // 克重
      'materialMeterOutput', // 出米数
      'meterOutput', // 出米数
      'paperBucket', // 纸筒
      'emptyGap', // 空差
      'weight', // 净重
      'materialNetWeight', // 净重
      ...(otherFields ?? []),
    ].includes(field)
  );
};

export const toFraction = (val: number | string) => {
  return new Fraction(val).simplify().toFraction(true);
};
export const toFloat = (val: string) => {
  return formatter(new Fraction(val).valueOf(), 2, false);
};
